import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Film, Globe, Plus, Trash2, Upload } from '../../icons';
import {
  Badge, Button, Chip, EmptyState, FormField, Input, ListPane, ListPaneItem,
  Modal, ModalActions, ModalFootNote, Select, Switch, Table, Tabs,
} from '../../components/shared';
import {
  createChannel, createVideo, deleteVideo, listChannelVideos, listChannels,
  readVideoDuration, updateChannel, updateVideo, uploadVideoFile,
  type BroadcastChannelDto, type BroadcastVideoDto, type VideoSource, type WidgetMode,
} from '../../api/broadcast';
import { NewGroupModal } from './NewGroupModal';
import './NoticeSlide.css';
import './Broadcast.css';

/**
 * 통합설정 › 대시보드 › **사내 방송** (2026-09-08 · migration 174 · 시안 화면 K).
 *
 * ★★★ **파일과 링크가 한 목록에 산다.** 사람에게 둘은 같은 것이다 — 한 자리에서 순서를 정하고
 *     같은 기간을 갖고 같은 위젯에 나간다.
 *
 * ★★★ **서버에 변환기가 없다 — 올린 그대로 나간다.** 그래서 등록 창은 고른 파일의 길이·용량으로
 *     화질을 **미리 말해 주고**(시안의 판정 카드), 상한(20MB · 5분)을 넘을 때만 막으며,
 *     막을 때는 「URL 등록이 맞다」고 갈 길을 알려 준다.
 *
 * ★★ 링크는 **채널이 허용한 도메인**만 걸린다. 비어 있으면 아무것도 못 건다 —
 *    「비었으니 다 된다」로 두면 회사가 아무것도 안 정한 상태에서 사내 첫 화면에 바깥 광고가 뜬다.
 */

const MB = 1024 * 1024;
const FILE_MAX = 20 * MB;
const FILE_MAX_SEC = 300;

const WIDGET_MODE_OPTIONS: Array<{ value: WidgetMode; label: string }> = [
  { value: 'LATEST', label: '최신 1건' },
  { value: 'PLAYLIST', label: '재생목록' },
  { value: 'PICKED', label: '지정 영상' },
];

const mmss = (sec: number | null | undefined) =>
  (sec == null ? '길이 모름' : `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`);
const mb = (b: number | null | undefined) => (b == null ? '' : `${(b / MB).toFixed(1)}MB`);
const ymd = (iso: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const startOfDay = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const endOfDay = (d: string) => new Date(`${d}T23:59:59`).toISOString();

/** 화면이 미리 보여 주는 판정 — 서버(`broadcast-rules.judgeQuality`)와 **같은 셈**을 쓴다. */
const AUDIO_MBPS = 0.064;
function judge(sizeBytes: number, durationSec: number | null): { mbps: number; label: string; ok: boolean } {
  if (durationSec == null || durationSec <= 0) return { mbps: 0, label: '길이 모름', ok: true };
  const mbps = Math.max(0, (sizeBytes * 8) / durationSec / 1_000_000 - AUDIO_MBPS);
  if (mbps >= 1.2) return { mbps, label: '720p 보통', ok: true };
  if (mbps >= 0.75) return { mbps, label: '480p 괜찮음', ok: true };
  if (mbps >= 0.55) return { mbps, label: '360p 보통', ok: true };
  if (mbps >= 0.3) return { mbps, label: '360p 하한', ok: true };
  return { mbps, label: '못 봄', ok: false };
}

function phaseOf(v: BroadcastVideoDto, now = Date.now()): { label: string; variant: 'success' | 'warning' | 'default' } {
  if (now < new Date(v.startsAt).getTime()) return { label: '게시 예정', variant: 'warning' };
  if (now >= new Date(v.endsAt).getTime()) return { label: '게시 끝', variant: 'default' };
  return { label: '게시 중', variant: 'success' };
}

interface Draft {
  source: VideoSource;
  title: string;
  url: string;
  from: string;
  to: string;
  file: File | null;
  fileDuration: number | null;
}

function emptyDraft(source: VideoSource): Draft {
  const today = new Date();
  const later = new Date(today.getTime() + 30 * 86_400_000);
  return {
    source, title: '', url: '',
    from: today.toISOString().slice(0, 10), to: later.toISOString().slice(0, 10),
    file: null, fileDuration: null,
  };
}

export function BroadcastSection() {
  const [channels, setChannels] = useState<BroadcastChannelDto[]>([]);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [videos, setVideos] = useState<BroadcastVideoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ordering, setOrdering] = useState(false);

  const [open, setOpen] = useState(false);
  /** 「채널 만들기」 창 (2026-09-09) — 종전에는 `window.prompt` 였다. */
  const [newOpen, setNewOpen] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft('FILE'));
  const [formError, setFormError] = useState<string | null>(null);
  const [hostDraft, setHostDraft] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cur = useMemo(() => channels.find((c) => c.id === channelId) ?? null, [channels, channelId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listChannels();
      setChannels(list);
      setChannelId((prev) => prev ?? list[0]?.id ?? null);
      setError(null);
    } catch {
      setError('채널을 불러오지 못했습니다');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!channelId) { setVideos([]); return; }
    let alive = true;
    void listChannelVideos(channelId)
      .then((rows) => { if (alive) setVideos(rows); })
      .catch(() => { if (alive) setError('영상을 불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [channelId]);

  const reload = useCallback(async () => {
    if (!channelId) return;
    setVideos(await listChannelVideos(channelId));
    setChannels(await listChannels());
  }, [channelId]);

  /**
   * ★★★ 종전에는 `window.prompt('새 채널 이름')` 이었다 — 테마도 글꼴도 안 따라가고,
   *     **이름 말고는 아무것도 못 정했다**(위젯에 걸지도 못 골랐다).
   */
  const addChannel = async (name: string, isWidget: boolean) => {
    setBusy(true);
    setNewError(null);
    try {
      const created = await createChannel(name, isWidget);
      await load();
      setChannelId(created.id);
      setNewOpen(false);
    } catch { setNewError('채널을 만들지 못했습니다'); } finally { setBusy(false); }
  };

  const openNewChannel = () => { setNewError(null); setNewOpen(true); };

  const patchChannel = async (patch: Parameters<typeof updateChannel>[1]) => {
    if (!cur) return;
    setBusy(true);
    try {
      const next = await updateChannel(cur.id, patch);
      setChannels((list) => list.map((c) => (c.id === next.id ? { ...c, ...next } : c)));
    } catch { setError('채널 설정을 저장하지 못했습니다'); } finally { setBusy(false); }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const next = [...videos];
    const to = index + dir;
    if (to < 0 || to >= next.length) return;
    [next[index]!, next[to]!] = [next[to]!, next[index]!];
    setVideos(next);
    setBusy(true);
    try {
      await Promise.all(next.map((v, i) => (v.sortOrder === i ? null : updateVideo(v.id, { sortOrder: i }))));
      await reload();
    } catch { setError('순서를 저장하지 못했습니다'); await reload(); } finally { setBusy(false); }
  };

  const remove = async (v: BroadcastVideoDto) => {
    if (!window.confirm(`「${v.title}」 영상을 지울까요? 되돌릴 수 없습니다.`)) return;
    setBusy(true);
    try { await deleteVideo(v.id); await reload(); }
    catch { setError('지우지 못했습니다'); } finally { setBusy(false); }
  };

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    const durationSec = await readVideoDuration(file);
    setDraft((d) => ({ ...d, file, fileDuration: durationSec, title: d.title || file.name.replace(/\.[^.]+$/, '') }));
    setFormError(null);
  };

  const verdict = draft.file ? judge(draft.file.size, draft.fileDuration) : null;
  const overSize = Boolean(draft.file && draft.file.size > FILE_MAX);
  const overLen = Boolean(draft.fileDuration != null && draft.fileDuration > FILE_MAX_SEC);

  const save = async () => {
    if (!cur) return;
    if (!draft.title.trim()) { setFormError('제목을 적어 주세요'); return; }
    if (!draft.from || !draft.to) { setFormError('게시 기간을 정해 주세요'); return; }
    setBusy(true);
    try {
      if (draft.source === 'FILE') {
        if (!draft.file) { setFormError('올릴 파일을 골라 주세요'); return; }
        if (overSize || overLen) {
          setFormError('20MB · 5분을 넘습니다 — 이 길이는 URL 등록이 맞습니다');
          return;
        }
        const { fileKey, sizeBytes } = await uploadVideoFile(draft.file);
        await createVideo(cur.id, {
          source: 'FILE', title: draft.title.trim(), fileKey, sizeBytes,
          contentType: draft.file.type || 'video/mp4', durationSec: draft.fileDuration,
          startsAt: startOfDay(draft.from), endsAt: endOfDay(draft.to),
        });
      } else {
        if (!draft.url.trim()) { setFormError('영상 주소를 적어 주세요'); return; }
        await createVideo(cur.id, {
          source: 'URL', title: draft.title.trim(), url: draft.url.trim(),
          startsAt: startOfDay(draft.from), endsAt: endOfDay(draft.to),
        });
      }
      setOpen(false);
      await reload();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg ?? '등록하지 못했습니다');
    } finally { setBusy(false); }
  };

  const columns = [
    {
      key: 'video', header: '영상', rowHeader: true,
      render: (v: BroadcastVideoDto) => (
        <span className="nsl-cell">
          <span className="bcs-thumb" aria-hidden>{v.durationSec != null && <i>{mmss(v.durationSec)}</i>}</span>
          <span className="nsl-cell__meta">
            <b className="nsl-cell__title">{v.title}</b>
            <span className="nsl-cell__sub">
              {v.source === 'URL' ? v.url : `${mb(v.sizeBytes)}${v.quality ? ` · ${v.quality.label}` : ''}`}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'source', header: '출처', width: '110px',
      render: (v: BroadcastVideoDto) => (
        <Chip inline size="sm" lead={v.source === 'URL' ? <Globe size={12} /> : <Film size={12} />}>
          {v.source === 'URL' ? 'URL' : 'MP4'}
        </Chip>
      ),
    },
    {
      key: 'period', header: '게시 기간', width: '190px',
      render: (v: BroadcastVideoDto) => <span className="nsl-num">{ymd(v.startsAt)} ~ {ymd(v.endsAt)}</span>,
    },
    {
      key: 'phase', header: '상태', width: '110px',
      render: (v: BroadcastVideoDto) => {
        const p = phaseOf(v);
        return <Badge variant={p.variant} size="sm">{p.label}</Badge>;
      },
    },
    ordering
      ? {
        key: 'order', header: '순서', width: '110px',
        render: (v: BroadcastVideoDto, i: number) => (
          <span className="nsl-acts">
            <Button variant="secondary" size="sm" iconOnly disabled={i === 0 || busy}
              aria-label={`${v.title} 위로`} onClick={() => { void move(i, -1); }}>
              <ChevronUp size={14} aria-hidden />
            </Button>
            <Button variant="secondary" size="sm" iconOnly disabled={i === videos.length - 1 || busy}
              aria-label={`${v.title} 아래로`} onClick={() => { void move(i, 1); }}>
              <ChevronDown size={14} aria-hidden />
            </Button>
          </span>
        ),
      }
      : {
        key: 'manage', header: '관리', width: '120px',
        render: (v: BroadcastVideoDto) => (
          <span className="nsl-acts">
            <Button variant="secondary" size="sm" iconOnly aria-label={`${v.title} 삭제`} disabled={busy}
              onClick={() => { void remove(v); }}>
              <Trash2 size={14} aria-hidden />
            </Button>
          </span>
        ),
      },
  ];

  return (
    <section className="svc-cat svc-cat--menu">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Film size={17} /></span>
        <span className="svc-cat__title">사내 방송</span>
        <span className="svc-cat__count">{channels.length}</span>
        <span className="svc-cat__desc">
          대시보드 <b>사내 방송</b> 위젯이 보여 줄 영상 — 올린 파일과 걸어 둔 링크가 한 목록에 있습니다
        </span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <div className="nsl">
        <ListPane
          title="채널" count={channels.length} ariaLabel="채널"
          action={<Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={openNewChannel}>채널 추가</Button>}
          footer={cur && (
            <dl className="nsl-sum">
              <div><dt>이 채널 용량</dt><dd>{mb(cur.usedBytes)} / {(cur.quotaBytes / (1024 ** 3)).toFixed(0)}GB</dd></div>
              <div><dt>영상</dt><dd>{cur.videoCount ?? videos.length}개</dd></div>
              <div><dt>대시보드 위젯</dt><dd>{cur.isWidget ? '이 채널' : '아니오'}</dd></div>
            </dl>
          )}
        >
          {channels.map((c) => (
            <ListPaneItem
              key={c.id} name={c.name} selected={c.id === channelId}
              badge={c.isWidget ? <Badge variant="success" size="sm">위젯</Badge> : undefined}
              onClick={() => { setChannelId(c.id); setOrdering(false); }}
            />
          ))}
        </ListPane>

        <div className="nsl-work">
          {!cur && !loading && (
            <EmptyState
              icon={Film} title="채널이 없습니다"
              description="채널을 하나 만들면 대시보드 위젯이 그 채널의 최신 영상을 보여 줍니다."
              action={<Button variant="primary" iconLeft={<Plus size={14} />} onClick={openNewChannel}>채널 만들기</Button>}
            />
          )}

          {cur && (
            <>
              <div className="nsl-work__head">
                <span className="nsl-work__name">{cur.name}</span>
                {cur.isWidget && <Badge variant="info" size="sm">위젯이 이 채널을 봅니다</Badge>}
                <span className="nsl-work__sp" />
                <Button variant="secondary" size="sm" disabled={busy || videos.length < 2} onClick={() => setOrdering((v) => !v)}>
                  {ordering ? '순서변경 끝' : '순서변경'}
                </Button>
                <Button variant="primary" size="sm" iconLeft={<Plus size={14} />} disabled={busy}
                  onClick={() => { setDraft(emptyDraft('FILE')); setFormError(null); setOpen(true); }}>
                  영상 추가
                </Button>
              </div>

              {videos.length === 0
                ? <EmptyState icon={Film} title="영상이 없습니다" description="파일을 올리거나 주소를 걸면 위젯에 나갑니다." />
                : <Table caption={`${cur.name} 영상`} columns={columns} rows={videos} rowKey={(v) => v.id} />}

              <div className="nsl-rules">
                <h4 className="nsl-rules__h">채널 정책</h4>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">위젯에 보일 것</span>
                  <Select ariaLabel="위젯에 보일 것" value={cur.widgetMode} size="sm" disabled={busy}
                    onChange={(v) => { void patchChannel({ widgetMode: v as WidgetMode }); }}
                    options={WIDGET_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                  <Switch checked={cur.isWidget} disabled={busy}
                    onCheckedChange={(v) => { void patchChannel({ isWidget: v }); }}
                    label="이 채널을 위젯에 건다" />
                </div>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">자동 재생</span>
                  {/* ★ 라벨은 «무엇을 켜고 끄는지»다(상태를 적지 않는다 · 정본 B7).
                        ★★★ 자동 재생은 **소리 없이** 다 — 브라우저가 소리 나는 자동 재생을 막고,
                             대시보드에서 소리가 나면 사람들은 위젯을 지운다. 둘의 방향이 같다. */}
                  <Switch checked={cur.autoplay} disabled={busy}
                    onCheckedChange={(v) => { void patchChannel({ autoplay: v }); }}
                    label="열자마자 소리 없이 재생한다" />
                  <span className="nsl-rules__note">
                    <b>대시보드에서 소리가 나면 사람들은 위젯을 지웁니다.</b> 기본은 꺼 둡니다.
                    켜면 <b>소리 없이</b> 시작하고, 보는 사람이 「소리 켜기」를 누르면 들립니다 —
                    브라우저가 소리 나는 자동 재생을 막습니다.
                  </span>
                </div>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">이어보기</span>
                  <Switch checked={cur.resume} disabled={busy}
                    onCheckedChange={(v) => { void patchChannel({ resume: v }); }}
                    label={cur.resume ? '본 지점을 기억합니다' : '늘 처음부터'} />
                  {/* ★ 못 하는 사정을 «미리» 말한다 — 끼워 넣은 재생기(YouTube · Vimeo)는
                         「지금 몇 초」를 알려 주지 않아 **새 지점을 적지 못한다**. 시작만 맞춘다. */}
                  <span className="nsl-rules__note">
                    기억한 지점은 <b>기기를 옮겨도 따라옵니다</b>. 다만 <b>링크 영상</b>(YouTube 등)은
                    이어볼 지점에서 <b>시작만</b> 하고 새 지점은 적지 못합니다.
                  </span>
                </div>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">허용 도메인</span>
                  <span className="bcs-hosts">
                    {cur.allowedHosts.map((h) => (
                      <Chip inline key={h} size="sm" onRemove={() => { void patchChannel({ allowedHosts: cur.allowedHosts.filter((x) => x !== h) }); }} removeLabel={`${h} 빼기`}>
                        {h}
                      </Chip>
                    ))}
                    <Input value={hostDraft} placeholder="youtube.com" wrapperClassName="bcs-hostinput"
                      aria-label="허용 도메인 추가"
                      onChange={(e) => setHostDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' || !hostDraft.trim()) return;
                        e.preventDefault();
                        void patchChannel({ allowedHosts: [...cur.allowedHosts, hostDraft.trim()] });
                        setHostDraft('');
                      }} />
                  </span>
                  <span className="nsl-rules__note">
                    ★ <b>비워 두면 링크를 하나도 걸 수 없습니다.</b> 아무 주소나 걸리면 사내 첫 화면에
                    바깥 광고가 뜹니다 — 여는 것은 사람이 한 번 정하는 일이어야 합니다.
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {newOpen && (
        <NewGroupModal
          title="채널 만들기"
          namePlaceholder="예: 대표님 타운홀"
          flagLabel="이 채널을 위젯에 건다"
          flagHint="켜면 대시보드 「사내 방송」 위젯이 이 채널의 영상을 보여 줍니다. 걸 수 있는 채널은 하나입니다."
          isFirst={channels.length === 0}
          footNote="재생 방식 · 이어보기 · 허용 도메인은 만든 뒤 오른쪽에서 정합니다."
          busy={busy}
          error={newError}
          onClose={() => setNewOpen(false)}
          onSubmit={(name, isWidget) => { void addChannel(name, isWidget); }}
        />
      )}

      {open && cur && (
        <Modal
          title={`영상 추가 — ${cur.name}`} onClose={() => setOpen(false)} size="md" accent
          footer={(
            <>
              <ModalFootNote>등록하면 <b>{cur.name}</b> 채널 맨 뒤에 놓입니다</ModalFootNote>
              <ModalActions>
                <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>취소</Button>
                <Button variant="primary" onClick={() => { void save(); }} loading={busy}>등록</Button>
              </ModalActions>
            </>
          )}
        >
          <Tabs
            ariaLabel="등록 방식" variant="pill" value={draft.source}
            items={[{ value: 'FILE', label: '파일 올리기' }, { value: 'URL', label: 'URL 등록' }]}
            onChange={(v) => setDraft((d) => ({ ...d, source: v as VideoSource }))}
          />

          {formError && <div className="admin-console__alert admin-console__alert--warn" role="alert">{formError}</div>}

          {draft.source === 'FILE' ? (
            <>
              <div className="bcs-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); void pickFile(e.dataTransfer.files?.[0]); }}>
                <Film size={26} aria-hidden />
                <b>{draft.file ? draft.file.name : '영상 파일을 끌어다 놓으세요'}</b>
                <span>.mp4 (H.264) · <b>최대 20MB · 5분 이하</b> · 360p 권장</span>
                <Button variant="secondary" size="sm" iconLeft={<Upload size={13} />} onClick={() => fileRef.current?.click()}>
                  파일 고르기
                </Button>
                <input ref={fileRef} type="file" accept="video/mp4" hidden
                  onChange={(e) => { void pickFile(e.target.files?.[0]); e.target.value = ''; }} />
              </div>

              {draft.file && (
                <div className={`bcs-verdict${overSize || overLen || verdict?.ok === false ? ' is-bad' : ' is-ok'}`}>
                  <AlertTriangle size={16} aria-hidden />
                  <div>
                    {overSize || overLen
                      ? (
                        <>
                          <b>등록할 수 없습니다.</b> {mb(draft.file.size)} · {mmss(draft.fileDuration)} —
                          상한(20MB · 5분)을 넘습니다. <b>이 길이는 URL 등록이 맞습니다</b> — 사내 스트리밍이나
                          비공개 링크에 올리고 주소만 거세요.
                          <div className="bcs-verdict__act">
                            <Button variant="secondary" size="sm" iconLeft={<Globe size={13} />}
                              onClick={() => setDraft((d) => ({ ...d, source: 'URL' }))}>
                              URL 등록으로 옮기기
                            </Button>
                          </div>
                        </>
                      )
                      : (
                        <>
                          <b>{verdict?.ok === false ? '올려도 아무도 못 봅니다.' : '등록할 수 있습니다.'}</b>{' '}
                          {mmss(draft.fileDuration)} · {mb(draft.file.size)} → 영상 <b>{verdict?.mbps.toFixed(2)}Mbps</b> →{' '}
                          <b>{verdict?.label}</b>. 서버에 변환기가 없어 <b>올린 그대로 나갑니다</b>.
                        </>
                      )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <FormField label="영상 주소" required hint="채널이 허용한 도메인만 걸 수 있습니다.">
                <Input value={draft.url} fullWidth placeholder="https://youtu.be/…"
                  onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))} />
              </FormField>
              <div className="bcs-allow">
                {cur.allowedHosts.length === 0
                  ? <span className="bcs-allow__none">허용 도메인이 없습니다 — 채널 정책에서 먼저 정해 주세요</span>
                  : cur.allowedHosts.map((h) => <Chip inline key={h} size="sm">{h}</Chip>)}
              </div>
              <p className="nsl-note">
                <b>링크는 우리 서버 용량을 쓰지 않습니다.</b> 대신 <b>그쪽에서 지우면 우리 화면에서도
                사라집니다</b> — 중요한 영상은 파일로 올려 두는 편이 안전합니다.
              </p>
            </>
          )}

          <FormField label="제목" required>
            <Input value={draft.title} maxLength={120} fullWidth
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
          </FormField>

          <div className="nsl-form2">
            <FormField label="시작" required>
              <Input type="date" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </FormField>
            <FormField label="끝" required hint="지나면 목록에만 남고 위젯에서 빠집니다.">
              <Input type="date" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </FormField>
          </div>
        </Modal>
      )}
    </section>
  );
}
